import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CommandPaletteCommand, CommandPaletteProps } from '../../../types/DesignSystem/CommandPalette';
import Kbd, { keysOf } from '../Kbd/Kbd';
import { Search } from '../Iconography/Icons';
import { useDismissable } from '../useDismissable';
import { useMergedRef } from '../useMergedRef';
import { usePresence } from '../usePresence';
import { MOTION, exitDurationFor } from '../motion';
import { withClassName } from '../cn';

/**
 * CommandPalette (§ 305) — see the type file for the contract.
 *
 * **It is a Dialog in every way that matters** — scrim on the `overlay` rung, modal
 * focus cycle, body-scroll lock, focus back to the opener on close — and it shares
 * Dialog's motion; but it is top-anchored (15vh down, where every command palette
 * sits, so the list has room to grow downward) and its whole chrome is one input row,
 * one list and one hint strip. Nothing in it is a tab stop except the input: the
 * options are `<div role="option">`, highlighted by `aria-activedescendant`, so the
 * user keeps typing mid-navigation — the Combobox model, and the reason this does not
 * reuse `listKeyboardNav`.
 */

const slug = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

/** 0 = label starts with the query, 1 = label contains it, 2 = a secondary field does, -1 = no match. */
const rank = (command: CommandPaletteCommand, query: string): number => {
  if (query === '') return 1;
  const label = command.label.toLowerCase();
  if (label.startsWith(query)) return 0;
  if (label.includes(query)) return 1;
  const secondary = [command.group ?? '', command.description ?? '', ...(command.keywords ?? [])].join(' ').toLowerCase();
  return secondary.includes(query) ? 2 : -1;
};

const CommandPalette = React.forwardRef<HTMLInputElement, CommandPaletteProps>(function CommandPalette({
  open,
  onOpenChange,
  commands,
  onSelect,
  placeholder = 'Type a command or search…',
  emptyText = 'No matching commands',
  ariaLabel,
  'aria-label': ariaLabelNative,
  className = '',
  'data-testid': dataTestId,
  ...rest
}, forwardedRef) {
  const uid = React.useId();
  const listboxId = `${uid}-listbox`;
  /* § 354 — the native spelling wins; it used to arrive in `rest` and be overwritten by
     the default. The default still names a palette given neither. */
  const paletteName = ariaLabelNative ?? ariaLabel ?? 'Command palette';
  const optionId = (id: string) => `${uid}-opt-${slug(id)}`;
  const panelRef = useRef<HTMLDivElement>(null);
  /* § 410 — part of the layer: stays live while the page behind is inert. */
  const backdropRef = useRef<HTMLDivElement>(null);
  const inputRowRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const setInputRef = useMergedRef<HTMLInputElement>(inputRef, forwardedRef);
  const activeRef = useRef<HTMLDivElement>(null);

  const { isPresent, phase } = usePresence(open, exitDurationFor('dialog'));
  const exiting = phase === 'exiting';

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  /*
    Two shapes, one list. BROWSING (no query): the commands in the caller's order, under
    their group headings, ungrouped rows first. SEARCHING: one flat list in rank order —
    a heading between the best match and the second-best would only hide the ranking.
    `visible` is the flattened DOM order in both shapes, so the highlight index, the
    arrows and `aria-activedescendant` mean the same thing as what the eye sees.
  */
  const { visible, groups } = useMemo(() => {
    const q = query.trim().toLowerCase();
    type Group = { name: string | undefined; items: CommandPaletteCommand[] };
    if (q === '') {
      const grouped: Group[] = [];
      commands.forEach((command) => {
        const existing = grouped.find((g) => g.name === command.group);
        if (existing) existing.items.push(command);
        else grouped.push({ name: command.group, items: [command] });
      });
      grouped.sort((a, b) => (a.name === undefined ? -1 : b.name === undefined ? 1 : 0));
      return { visible: grouped.flatMap((g) => g.items), groups: grouped };
    }
    const ranked = commands
      .map((command, order) => ({ command, order, score: rank(command, q) }))
      .filter((entry) => entry.score >= 0)
      .sort((a, b) => a.score - b.score || a.order - b.order)
      .map((entry) => entry.command);
    return { visible: ranked, groups: [{ name: undefined, items: ranked }] as Group[] };
  }, [commands, query]);

  const firstEnabled = (from: number, direction: 1 | -1): number => {
    for (let i = from; i >= 0 && i < visible.length; i += direction) {
      if (!visible[i].disabled) return i;
    }
    return -1;
  };

  /* A fresh open is a fresh search; a new query reseats the highlight on its first row. */
  useEffect(() => {
    if (open) {
      setQuery('');
    }
  }, [open]);
  const visibleKey = visible.map((c) => c.id).join('\0');
  useEffect(() => {
    setActiveIndex(firstEnabled(0, 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- visibleKey stands in for visible
  }, [visibleKey, open]);

  useEffect(() => {
    // jsdom has no scrollIntoView; the guard is the difference between a test and a TypeError.
    activeRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  const close = () => onOpenChange(false);

  useDismissable({
    isOpen: isPresent,
    onDismiss: close,
    modal: true,
    surface: panelRef,
    backdrop: backdropRef,
    /* The row, not the input: the hook focuses the first focusable INSIDE the region. */
    initialFocus: inputRowRef,
    closeOnEscape: open,
    isInside: (target) => !!panelRef.current?.contains(target),
    closeOnOutsidePointerDown: open,
  });

  const run = (command: CommandPaletteCommand) => {
    if (command.disabled) return;
    command.onSelect?.();
    onSelect?.(command);
    close();
  };

  const moveActive = (direction: 1 | -1) => {
    if (visible.length === 0) return;
    let next = activeIndex;
    for (let i = 0; i < visible.length; i += 1) {
      next = (next + direction + visible.length) % visible.length;
      if (!visible[next].disabled) break;
    }
    setActiveIndex(next);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown': event.preventDefault(); moveActive(1); break;
      case 'ArrowUp': event.preventDefault(); moveActive(-1); break;
      case 'Home': event.preventDefault(); setActiveIndex(firstEnabled(0, 1)); break;
      case 'End': event.preventDefault(); setActiveIndex(firstEnabled(visible.length - 1, -1)); break;
      case 'Enter': {
        event.preventDefault();
        const active = visible[activeIndex];
        if (active) run(active);
        break;
      }
      default:
    }
  };

  if (!isPresent) return null;

  const testid = (suffix: string) => (dataTestId ? `${dataTestId}-${suffix}` : undefined);
  const active = visible[activeIndex];

  const renderRow = (command: CommandPaletteCommand) => {
    const index = visible.indexOf(command);
    const isActive = index === activeIndex;
    return (
      <div
        key={command.id}
        id={optionId(command.id)}
        ref={isActive ? activeRef : undefined}
        role="option"
        aria-selected={isActive}
        aria-disabled={command.disabled || undefined}
        data-testid={testid(`option-${command.id}`)}
        onMouseDown={(event) => event.preventDefault()}
        onMouseMove={() => { if (!command.disabled && !isActive) setActiveIndex(index); }}
        onClick={() => run(command)}
        /* The highlight is the neutral hover ground — pointer and keyboard read as one
           (§ 51); disabled recedes to one ink (§ 228). */
        className={`tw-mx-2 tw-flex tw-items-center tw-gap-3 tw-rounded-ctl tw-px-3 tw-py-2 tw-text-body-primary tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${
          command.disabled
            ? 'tw-text-ink-disabled tw-cursor-not-allowed'
            : isActive
              ? 'tw-bg-surface-hover-strong tw-text-ink tw-cursor-pointer'
              : 'tw-text-ink-secondary tw-cursor-pointer'
        }`}
      >
        {command.icon && (
          <span aria-hidden="true" className={`tw-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center ${command.disabled ? '' : 'tw-text-ink-tertiary'}`}>
            {command.icon}
          </span>
        )}
        <span className="tw-min-w-0 tw-flex-1">
          <span className="tw-block tw-truncate">{command.label}</span>
          {command.description && (
            <span className={`tw-block tw-truncate tw-text-caption-metadata ${command.disabled ? '' : 'tw-text-ink-tertiary'}`}>
              {command.description}
            </span>
          )}
        </span>
        {command.shortcut && (
          <span aria-hidden="true" className="tw-flex tw-shrink-0 tw-items-center tw-gap-1">
            {keysOf(command.shortcut).map((key) => <Kbd key={key}>{key}</Kbd>)}
          </span>
        )}
      </div>
    );
  };

  return createPortal(
    <>
      <div
        data-testid={testid('backdrop')}
        ref={backdropRef}
        className={`tw-fixed tw-inset-0 ${MOTION.overlay.scrim} tw-z-overlay ${exiting ? MOTION.overlay.exit : MOTION.overlay.enter}`}
        aria-hidden="true"
      />
      {/* Top-anchored, not centred: a palette grows DOWN as the user types, and a
          centred one would jump with every keystroke. `font-noto-sans` because this
          tree portals into <body>, whose stack leads with the system face. */}
      <div className="tw-fixed tw-inset-0 tw-z-overlay tw-flex tw-items-start tw-justify-center tw-p-4 tw-pt-[15vh] tw-font-noto-sans">
        <div
          {...rest}
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={paletteName}
          data-testid={dataTestId}
          className={withClassName(`tw-flex tw-w-full tw-max-w-xl tw-flex-col tw-max-h-[70vh] tw-overflow-hidden tw-rounded-ovl tw-bg-surface-raised tw-shadow-ds-lg tw-border tw-border-edge tw-outline-none ${
            exiting ? MOTION.dialog.exit : MOTION.dialog.enter
          }`, className)}
        >
          <div ref={inputRowRef} className="tw-flex tw-h-14 tw-shrink-0 tw-items-center tw-gap-3 tw-border-b tw-border-edge tw-px-4">
            <Search size={20} aria-hidden="true" className="tw-shrink-0 tw-text-ink-tertiary" />
            <input
              ref={setInputRef}
              type="text"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded="true"
              aria-controls={listboxId}
              aria-activedescendant={active ? optionId(active.id) : undefined}
              aria-label={paletteName}
              value={query}
              placeholder={placeholder}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleKeyDown}
              data-testid={testid('input')}
              className="tw-min-w-0 tw-flex-1 tw-border-0 tw-bg-transparent tw-p-0 tw-text-body-primary tw-text-ink tw-outline-none placeholder:tw-text-ink-secondary"
            />
            <Kbd>Escape</Kbd>
          </div>

          <div
            id={listboxId}
            role="listbox"
            aria-label={paletteName}
            data-testid={testid('list')}
            /* `overscroll-contain` (§ 222): the list's wheel must not chain to the page. */
            className="tw-flex-1 tw-overflow-y-auto tw-overscroll-contain tw-py-2"
          >
            {groups.map((group) =>
              group.name === undefined ? (
                <React.Fragment key="__ungrouped">{group.items.map(renderRow)}</React.Fragment>
              ) : (
                <div key={group.name} role="group" aria-labelledby={`${uid}-group-${slug(group.name)}`} data-testid={testid(`group-${slug(group.name)}`)}>
                  <div id={`${uid}-group-${slug(group.name)}`} className="tw-px-5 tw-pb-1 tw-pt-2 tw-text-sub-title-label tw-text-ink-tertiary">
                    {group.name}
                  </div>
                  {group.items.map(renderRow)}
                </div>
              ),
            )}
            {visible.length === 0 && (
              <div className="tw-px-5 tw-py-6 tw-text-center tw-text-body-primary tw-text-ink-tertiary" data-testid={testid('empty')}>
                {emptyText}
              </div>
            )}
          </div>

          {/* § 296 — the recess role: one rung under the panel in both themes. */}
          <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-4 tw-border-t tw-border-edge tw-bg-surface-recess tw-px-4 tw-py-2 tw-text-caption-metadata tw-text-ink-tertiary">
            <span className="tw-flex tw-items-center tw-gap-1"><Kbd>ArrowUp</Kbd><Kbd>ArrowDown</Kbd> to navigate</span>
            <span className="tw-flex tw-items-center tw-gap-1"><Kbd>Enter</Kbd> to select</span>
            <span className="tw-flex tw-items-center tw-gap-1"><Kbd>Escape</Kbd> to close</span>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
});

export default CommandPalette;
